import React from 'react';

import {
    Bot,
    Library,
    PlugZap,
    Settings,
    Workflow,
} from 'lucide-react';

// Namensregistry, kein UI-Shim: bildet gespeicherte Fluent-Icon-Namen auf
// lucide-Komponenten ab und fällt auf die lucide-Registry zurück.
import { fluentIconComponent } from '@/components/fluent-icon-map';
import { cn } from '@/lib/utils';

/**
 * Grafik-Icon im Fluss-Diagramm. Größe und Grundfarbe kommen weiterhin aus den
 * SASS-Klassen (`.node-icon`, `.type-icon`, `.palette-icon` setzen `font-size`
 * und `color`), deshalb rendert lucide mit `1em` statt einer festen Pixelgröße.
 */
export const GraphIcon: React.FC<{
    icon: React.ComponentType<any>;
    className?: string;
    style?: React.CSSProperties;
}> = ({ icon: IconComponent, className, style }) => (
    <IconComponent
        size="1em"
        className={cn('inline-block align-middle', className)}
        style={style}
    />
);

/** Rückgabewerte von `getTypeIcon` (ToolTypes.ts) → lucide-Komponente. */
const TYPE_ICONS: Record<string, React.ComponentType<any>> = {
    Flow: Workflow,
    PlugConnected: PlugZap,
    Library: Library,
    ChatBot: Bot,
    Settings: Settings,
};

/** Werkzeugart (Workflow, MCP, Wissensbasis, Prompt) als lucide-Komponente. */
export const resolveTypeIcon = (name: string): React.ComponentType<any> =>
    TYPE_ICONS[name] ?? Settings;

/**
 * Löst einen gespeicherten Werkzeug-Icon-Namen (Icon-Auswahl am Marktplatz-
 * Angebot, siehe `FluentIconSelector`) auf. Erste Instanz ist die
 * Namensregistry `fluentIconComponent`: sie kennt die 128 Fluent-Altnamen
 * (`CityNext`, `PlugConnected`, `Library` …), die in Kundendaten gespeichert
 * sind, und fällt selbst auf die lucide-Registry zurück, in der die
 * Icon-Auswahl ihre heutigen Namen ablegt. Danach greifen die Werkzeugart-
 * Namen, die der Registry fehlen (`Flow`, `ChatBot`); alles Übrige fällt auf
 * `Settings` zurück.
 */
export const resolveToolIcon = (name?: string | null): React.ComponentType<any> => {
    if (!name) return Settings;
    return fluentIconComponent(name)
        ?? TYPE_ICONS[name]
        ?? Settings;
};

// BPMN icon class mapping (uses bpmn-js bpmn-font, see bpmn-embedded.css)
export const BPMN_ICON_MAP: Record<string, string> = {
    pool: 'bpmn-icon-participant',
    start: 'bpmn-icon-start-event-none',
    end: 'bpmn-icon-end-event-none',
    xor: 'bpmn-icon-gateway-xor',
    and: 'bpmn-icon-gateway-parallel',
    manual: 'bpmn-icon-user',
    'mcp-tool': 'bpmn-icon-service',
    'knowledge-base': 'bpmn-icon-data-store',
    'prompt-tool': 'bpmn-icon-script',
    'workflow-tool': 'bpmn-icon-subprocess-collapsed',
};

// Palette item component - mouse-based drag (no HTML5 DnD, avoids browser ghost)
export interface PaletteItemProps {
    type: 'agent' | 'tool' | 'start' | 'end' | 'xor' | 'and' | 'mcp-tool' | 'knowledge-base' | 'prompt-tool' | 'workflow-tool' | 'manual' | 'pool';
    label: string;
    onMouseDown?: (event: React.MouseEvent, nodeType: string) => void;
    onDragStart?: (event: React.DragEvent, nodeType: string) => void;
}

export const PaletteItem: React.FC<PaletteItemProps> = ({
    type,
    label,
    onMouseDown,
    onDragStart
}) => {
    const bpmnIconClass = BPMN_ICON_MAP[type] || 'bpmn-icon-task';
    return (
        <div
            className={`palette-item palette-item-icon ${type}`}
            draggable={!!onDragStart}
            onMouseDown={onMouseDown ? (event) => onMouseDown(event, type) : undefined}
            onDragStart={onDragStart ? (event) => onDragStart(event, type) : undefined}
            title={label}
        >
            <span className={`bpmn-icon ${bpmnIconClass}`} aria-hidden />
        </div>
    );
};
